<template>
  <messenger-popover-layout>
    <div class="text-center">
      <div
        class="comment-rate mb-4"
        :class="`comment-rate--${rate}`"
      >
        <template v-if="isGood">
          <widget-icon
            name="thumb_up"
            size="24px"
          />
        </template>

        <template v-else>
          <widget-icon
            name="thumb_down"
            size="24px"
          />
        </template>
      </div>

      <div class="survey-title">
        <span v-if="isGood">{{ $t('chat.weAreGladYouLikedIt') }}</span>
        <span v-else>{{ $t('chat.wereSorryToHearThat') }}</span>
      </div>

      <div class="neutral--text lh-20 mb-6">
        {{ $t('chat.weAreAlwaysStrivingToImprove') }}
      </div>
    </div>

    <widget-form-line
      class="mb-3"
      :label="isGood ? $t('chat.tellUsWhereWeDidGood') : $t('chat.tellUsHowWeCanImprove')"
    >
      <widget-form-field-textarea
        name="comment"
        :placeholder="isGood ? $t('chat.whatDidYouLike') : $t('chat.whatDidYouDislike')"
        size="80px"
      />
    </widget-form-line>

    <div class="text-center">
      <div class="d-flex align-center flex-column">
        <widget-button
          v-bind="themeButtonProps"
          class="full-width"
          :is-loading="isLoading"
          :loading-title="$t('common.sending')"
          :is-disabled="!commentValue"
          @click="submit"
        >
          {{ $t('chat.sendFeedback') }}
        </widget-button>

        <widget-link
          class="mt-6"
          size="12"
          variant="secondary"
          @click="onSkip()"
        >
          {{ $t('chat.skipForNow') }}
        </widget-link>
      </div>
    </div>
  </messenger-popover-layout>
</template>

<script setup lang="ts">
import MessengerPopoverLayout from '@widgets/messenger/components/popovers/MessengerPopoverLayout.vue'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import type { Popovers } from '@widgets/messenger/types/popover'
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import WidgetFormLine from '@widgets/shared/components/WidgetFormLine.vue'
import WidgetFormFieldTextarea from '@widgets/shared/components/fields/WidgetFormFieldTextarea.vue'
import type { MessengerChatSurveyCommentForm } from '@widgets/messenger/types'
import WidgetLink from '@widgets/shared/components/WidgetLink.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import { computed, ref } from '@/composition/vue/compositionApi'
import { useWidgetForm, type WidgetFieldsConfig } from '@/composition/widgetForm'

const props = defineProps<Popovers['satisfactionSurveyComment']>()

const { themeButtonProps } = useWidgetTheme()

const isGood = computed(() => props.rate === 'good')

const isLoading = ref(false)

const fieldsConfig = {
  comment: {
    value: '',
  },
} satisfies WidgetFieldsConfig<MessengerChatSurveyCommentForm>

const { submit, form } = useWidgetForm<MessengerChatSurveyCommentForm>(fieldsConfig, async (formData) => {
  isLoading.value = true
  try {
    await props.onSendComment(formData.comment)
  } finally {
    isLoading.value = false
  }
})

const commentValue = computed(() => form.comment)
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';

.comment-rate {
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;

  &--good {
    background: $green100;
    color: $green;
  }

  &--poor {
    background: $red100;
    color: $red;
  }
}

.survey-title {
  font-size: 20px;
  line-height: 24px;
  font-weight: $bold-font;
  margin-bottom: 8px;
}
</style>
